// Pure helper that classifies a deadline date string into an urgency tier
// (color + icon + tooltip data) for the "deadline as urgency" UI treatment.

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_HOUR = 60 * 60 * 1000;

// Thresholds are measured in full days left until the deadline.
const RED_MAX_DAYS_LEFT = 2;
const YELLOW_MAX_DAYS_LEFT = 5;

/**
 * @param {string} dateString - raw field value as sent by the API, same value that goes
 *   into the `formatDate` filter: "YYYY-MM-DD" or "YYYY-MM-DD HH:MM".
 * @param {Date} [now] - reference point, defaults to current time (injectable for tests).
 * @returns {{tier: 'red'|'yellow'|'grey', color: string, icon: string, overdue: boolean, daysLeft: number, hoursLeft: number}|null}
 *   null when the date is empty/invalid, so callers can safely render a no-op (plain date, no icon).
 */
export default function deadlineUrgency(dateString, now = new Date()) {
  if (!dateString) return null;

  let [datePart, timePart] = dateString.trim().split(' ');
  // No time part means "end of day" so that "today" isn't already overdue before midnight.
  let deadline = timePart ? new Date(`${datePart}T${timePart}`) : new Date(`${datePart}T23:59:59`);

  if (isNaN(deadline.getTime())) return null;

  let overdue = deadline.getTime() < now.getTime();
  let msLeft = deadline.getTime() - now.getTime();
  let daysLeft = Math.floor(msLeft / MS_PER_DAY);
  let hoursLeft = Math.floor(msLeft / MS_PER_HOUR);

  if (overdue || daysLeft <= RED_MAX_DAYS_LEFT) {
    return { tier: 'red', color: 'error', icon: 'mdi-alert-circle', overdue, daysLeft, hoursLeft };
  }
  if (daysLeft <= YELLOW_MAX_DAYS_LEFT) {
    return { tier: 'yellow', color: 'warning', icon: 'mdi-alert-circle-outline', overdue, daysLeft, hoursLeft };
  }
  return { tier: 'grey', color: 'grey', icon: 'mdi-clock-outline', overdue, daysLeft, hoursLeft };
}
